import { useState } from "react";
import { downloadFile } from "@/lib/utils/downloadUtils";
import { toast } from "sonner";
import { track, trackError } from "@/lib/analytics/track";
import type { JobStatusResponseDto } from "@/api";

type DownloadFileType = "image" | "gif";

interface UseResultDownloadProps {
  jobStatus?: JobStatusResponseDto;
  onDownloadComplete?: () => void;
  delayBeforeCallback?: number;
}

interface UseResultDownloadReturn {
  isDownloadingImage: boolean;
  isDownloadingGif: boolean;
  downloadImage: () => Promise<void>;
  downloadGif: () => Promise<void>;
  error: Error | null;
}

/**
 * Custom hook for handling photo result downloads
 * Runs the download cascade and surfaces the outcome through toasts,
 * analytics, and the error state, gating the completion callback on
 * outcomes that actually delivered the file.
 *
 * @param jobStatus - The job status containing file paths
 * @param onDownloadComplete - Optional callback to run after download completes
 * @param delayBeforeCallback - Delay in ms before running callback (default: 2000)
 * @returns Object containing download state and separate download functions for image and gif
 */
export const useResultDownload = ({
  jobStatus,
  onDownloadComplete,
  delayBeforeCallback = 2000,
}: UseResultDownloadProps): UseResultDownloadReturn => {
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingGif, setIsDownloadingGif] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const reportMissingFile = () => {
    const err = new Error("No downloadable file is available for this result.");
    setError(err);
    toast.error(err.message);
  };

  /**
   * Shared download flow: spinner state, cascade invocation, and outcome
   * handling (toasts + analytics) for both the image and the animation.
   */
  const runDownload = async (
    url: string,
    filename: string,
    mimeType: string | undefined,
    fileType: DownloadFileType,
    setIsDownloading: (value: boolean) => void
  ) => {
    setIsDownloading(true);
    setError(null);

    try {
      const outcome = await downloadFile(url, filename, mimeType);

      switch (outcome.status) {
        case "shared":
          track("download_share_opened", { file_type: fileType });
          break;
        case "downloaded":
          track("download_fallback_blob", { file_type: fileType });
          break;
        case "opened":
          track("download_fallback_open", { file_type: fileType });
          break;
        case "dismissed":
          track("download_share_dismissed", { file_type: fileType });
          return;
        case "hint":
          track("download_webview_hint", { file_type: fileType });
          toast(
            "This browser can’t save files directly. Long-press the photo or animation above to save it.",
            { duration: 8000 }
          );
          return;
        case "error":
          setError(outcome.error);
          toast.error(
            "Download failed. Please try again, or open this page in Safari or Chrome."
          );
          trackError({
            type: "download",
            message: outcome.error.message,
            source: "manual",
          });
          return;
      }

      // File reached the user — wait before running callback (e.g., showing modal)
      if (onDownloadComplete) {
        await new Promise((resolve) => setTimeout(resolve, delayBeforeCallback));
        onDownloadComplete();
      }
    } catch (err) {
      // downloadFile is designed never to reject; keep the spinner honest anyway.
      const unexpected = err instanceof Error ? err : new Error("Download failed");
      setError(unexpected);
      toast.error(
        "Download failed. Please try again, or open this page in Safari or Chrome."
      );
      trackError({ type: "download", message: unexpected.message, source: "manual" });
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadImage = async () => {
    if (!jobStatus?.outputPath) {
      reportMissingFile();
      return;
    }

    await runDownload(
      jobStatus.outputPath,
      "result.jpg",
      undefined,
      "image",
      setIsDownloadingImage
    );
  };

  const downloadGif = async () => {
    // Prefer the MP4 video; fall back to the GIF when no video was produced.
    if (jobStatus?.videoPath) {
      await runDownload(
        jobStatus.videoPath,
        "result.mp4",
        "video/mp4",
        "gif",
        setIsDownloadingGif
      );
      return;
    }

    if (jobStatus?.gifPath) {
      await runDownload(
        jobStatus.gifPath,
        "result.gif",
        "image/gif",
        "gif",
        setIsDownloadingGif
      );
      return;
    }

    reportMissingFile();
  };

  return {
    isDownloadingImage,
    isDownloadingGif,
    downloadImage,
    downloadGif,
    error,
  };
};
